import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Key, ShieldCheck, Eye, EyeOff, Mail, Save } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNotifications } from '@/hooks/useNotifications';
import { useChangePassword, useUpdateAdminProfile } from '@/hooks/useUsers';
import { useCurrentUser, useAppDispatch } from '@/hooks/redux';
import { setUser } from '@/store/slices/authSlice';
import { cn } from '@/utils';

// Form validation schema for password change
const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(6, 'New password must be at least 6 characters'),
    confirmPassword: z.string().min(1, 'Confirm password is required'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'New passwords do not match',
    path: ['confirmPassword'],
  });

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

const emptyDefaults: ChangePasswordFormData = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

// Form validation schema for email change
const changeEmailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ChangeEmailFormData = z.infer<typeof changeEmailSchema>;

export function ProfileSettingsPage() {
  const { success, error: showToastError } = useNotifications();
  const { mutate: changePass, isPending: isChanging } = useChangePassword();
  const { mutate: updateProfile, isPending: isUpdatingProfile } = useUpdateAdminProfile();

  const user = useCurrentUser();
  const dispatch = useAppDispatch();

  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  // Email form setup
  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    setValue: setEmailValue,
    formState: { errors: emailErrors, isDirty: isEmailDirty },
  } = useForm<ChangeEmailFormData>({
    resolver: zodResolver(changeEmailSchema),
    defaultValues: { email: user?.email || '' },
  });

  // Keep email form updated with user state
  React.useEffect(() => {
    if (user?.email) {
      setEmailValue('email', user.email);
    }
  }, [user, setEmailValue]);

  const onEmailSubmit = (data: ChangeEmailFormData) => {
    updateProfile(
      { email: data.email },
      {
        onSuccess: () => {
          success('Email updated successfully!');
          if (user) {
            dispatch(setUser({ ...user, email: data.email }));
          }
        },
        onError: (err) => {
          const errMsg = err.response?.data?.message || 'Failed to update email';
          showToastError(errMsg);
        },
      }
    );
  };

  // Password form setup
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: emptyDefaults,
  });

  const onSubmit = (data: ChangePasswordFormData) => {
    changePass(
      { oldPassword: data.currentPassword, newPassword: data.newPassword },
      {
        onSuccess: () => {
          success('Password updated successfully!');
          reset();
        },
        onError: (err) => {
          const errMsg = err.response?.data?.message || 'Failed to update password';
          showToastError(errMsg);
        },
      }
    );
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Profile Settings</h1>
        <p className="text-muted-foreground">
          Manage your administration profile, email address, and security settings.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Change Email Card */}
        <form onSubmit={handleSubmitEmail(onEmailSubmit)} className="flex flex-col h-full">
          <Card className="shadow-lg border-border bg-card flex flex-col h-full">
            <CardHeader className="bg-muted/30 border-b border-border/50">
              <div className="flex items-center space-x-2">
                <Mail className="h-5 w-5 text-primary" />
                <div>
                  <CardTitle>Change Email</CardTitle>
                  <CardDescription>Update your administration account email address</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4 flex-1 flex flex-col justify-between">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  disabled={isUpdatingProfile}
                  placeholder="Enter new email"
                  {...registerEmail('email')}
                  className={cn(emailErrors.email ? 'border-destructive' : '')}
                />
                {emailErrors.email && (
                  <p className="text-sm text-destructive">{emailErrors.email.message}</p>
                )}
              </div>

              <div className="border-t pt-4 flex justify-end">
                <Button
                  type="submit"
                  disabled={isUpdatingProfile || !isEmailDirty}
                  className="flex items-center gap-2"
                >
                  {isUpdatingProfile ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {isUpdatingProfile ? 'Saving...' : 'Save Email'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>

        {/* Change Password Card */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full">
          <Card className="shadow-lg border-border bg-card flex flex-col h-full">
            <CardHeader className="bg-muted/30 border-b border-border/50">
              <div className="flex items-center space-x-2">
                <Key className="h-5 w-5 text-primary" />
                <div>
                  <CardTitle>Change Password</CardTitle>
                  <CardDescription>
                    Enter your existing password and choose a new secure password
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-4 flex-1">
              <div className="space-y-2">
                <Label htmlFor="current-password">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    disabled={isChanging}
                    placeholder="Enter current password"
                    {...register('currentPassword')}
                    className={cn('pr-10', errors.currentPassword ? 'border-destructive' : '')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer focus:outline-none"
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.currentPassword && (
                  <p className="text-sm text-destructive">{errors.currentPassword.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    disabled={isChanging}
                    placeholder="Enter new password (min. 6 characters)"
                    {...register('newPassword')}
                    className={cn('pr-10', errors.newPassword ? 'border-destructive' : '')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer focus:outline-none"
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="text-sm text-destructive">{errors.newPassword.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    disabled={isChanging}
                    placeholder="Verify new password"
                    {...register('confirmPassword')}
                    className={cn('pr-10', errors.confirmPassword ? 'border-destructive' : '')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer focus:outline-none"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
                )}
              </div>

              <div className="border-t pt-4 flex justify-end">
                <Button type="submit" disabled={isChanging} className="flex items-center gap-2">
                  {isChanging ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="h-4 w-4" />
                  )}
                  {isChanging ? 'Updating...' : 'Update Password'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
}
